import type { ReactNode, Ref } from "react";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { cn } from "@shared-kernel/primary/shared/shadcn/utils/utils";
import { ChevronsUpDown, Check, Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@shared-kernel/primary/shared/shadcn/ui/popover";
import { Button } from "@shared-kernel/primary/shared/shadcn/ui/button";
import {
  Command,
  CommandInput,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@shared-kernel/primary/shared/shadcn/ui/command";
import { Skeleton } from "@shared-kernel/primary/shared/shadcn/ui/skeleton";
import clsx from "clsx";
import { getNormalizedString, sortAlphabetically } from "@utils/utils";
import Fuse from "fuse.js";

export interface ComboboxOptions {
  label: string;
  value: string;
  isDisabled?: boolean;
}

export interface ComboboxHandle {
  close: () => void;
}

const fuseOptions = {
  keys: ["label"],
  threshold: 0.3,
};

interface Props {
  options: ComboboxOptions[];
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  search: {
    commandInputPlaceHolder: string;
    notFoundText: string;
    notFoundAction?: ReactNode;
  };
  disabled?: boolean;
  disableSort?: boolean;
  dataTestId?: string;
  actionRef?: Ref<ComboboxHandle>;
  onOpenChange?: (isOpen: boolean) => void;
  isLoading?: boolean;
}

export const Combobox = forwardRef<HTMLButtonElement, Props>(
  (
    {
      value,
      options,
      onChange,
      search,
      placeholder,
      disabled = false,
      disableSort = false,
      dataTestId,
      actionRef,
      onOpenChange,
      isLoading = false,
    },
    ref,
  ) => {
    const [isOpen, setOpen] = useState(false);
    const orderedOptions = useMemo(
      () => (disableSort ? options : [...options].sort((a, b) => sortAlphabetically(a.label, b.label))),
      [options, disableSort],
    );
    const [filteredOptions, setFilteredOptions] = useState<ComboboxOptions[]>(orderedOptions);
    const [inputValue, setInputValue] = useState<string>("");
    const scrollableRef = useRef<HTMLDivElement>(null);

    const fuse = useMemo(() => new Fuse(orderedOptions, fuseOptions), [orderedOptions]);

    useImperativeHandle(
      actionRef,
      () => ({
        close: () => {
          setOpen(false);
          onOpenChange?.(false);
        },
      }),
      [onOpenChange],
    );

    useEffect(() => {
      const normalizedInputValue = getNormalizedString(inputValue);

      if (!normalizedInputValue) {
        setFilteredOptions(orderedOptions);
      } else {
        const results = fuse.search(normalizedInputValue);
        setFilteredOptions(results.map(result => result.item));
      }
    }, [fuse, inputValue, isOpen, orderedOptions]);

    useEffect(() => {
      scrollableRef.current?.scrollTo(0, 0);
    }, [filteredOptions, isOpen]);

    useEffect(() => {
      setInputValue("");
    }, [isOpen]);

    const handleOpenChange = (open: boolean) => {
      setOpen(open);
      onOpenChange?.(open);
    };

    return (
      <Popover open={isOpen} onOpenChange={handleOpenChange} modal>
        <PopoverTrigger asChild disabled={disabled}>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={isOpen}
            ref={ref}
            className="size-full justify-between overflow-hidden"
            data-testid={dataTestId ? `${dataTestId}-trigger` : undefined}
          >
            <span className="min-w-0 truncate">
              {value ? (
                orderedOptions.find(option => option.value === value)?.label
              ) : (
                <span className="font-normal opacity-50">{placeholder}</span>
              )}
            </span>
            {isLoading ? (
              <Loader2 className="ml-2 size-4 shrink-0 animate-spin opacity-50" />
            ) : (
              <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" modal>
          <Command shouldFilter={false}>
            <CommandInput placeholder={search.commandInputPlaceHolder} value={inputValue} onValueChange={setInputValue} />
            <CommandList ref={scrollableRef}>
              {isLoading ? (
                <div className="space-y-2 p-2">
                  <Skeleton className="h-8 w-full" />
                  <Skeleton className="h-8 w-full" />
                  <Skeleton className="h-8 w-full" />
                  <Skeleton className="h-8 w-full" />
                  <Skeleton className="h-8 w-full" />
                </div>
              ) : (
                <>
                  <CommandEmpty>
                    <div className="space-y-2">
                      <p>{search.notFoundText}</p>
                      {search.notFoundAction}
                    </div>
                  </CommandEmpty>
                  <CommandGroup>
                    {filteredOptions.map(option => (
                      <CommandItem
                        key={option.value}
                        value={option.value}
                        onSelect={currentValue => {
                          setOpen(false);
                          onChange(currentValue === value ? "" : currentValue);
                          setInputValue("");
                        }}
                        disabled={option.isDisabled}
                        className={clsx(option.isDisabled && "cursor-not-allowed text-muted-foreground")}
                        data-testid={dataTestId ? `${dataTestId}-option-${option.value}` : undefined}
                      >
                        <Check className={cn("mr-2 h-4 w-4", value === option.value ? "opacity-100" : "opacity-0")} />
                        {option.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  },
);

Combobox.displayName = "Combobox";
